import { Popover } from '@servicecompressionllc/sc-design-system';
import { Fragment } from 'react';
import { type PickerVariable } from '../definitionEdit.js';

/** Section header for one variable source — label + a quieter detail line. */
const sourceHeading = (
  s: PickerVariable['source'],
  eventSource: string | null,
): { label: string; detail: string | null } =>
  s === 'event'
    ? { label: 'From the event', detail: eventSource }
    : { label: `From step “${s.step}”`, detail: null };

/** Drops a value from the event into the message you're writing. */
export const VarPicker = ({
  vars,
  onPick,
  source,
}: {
  vars: PickerVariable[];
  onPick: (v: PickerVariable) => void;
  /** What the list is built from — null when the trigger has no registered fields. */
  source: string | null;
}) => {
  return (
    <Popover>
      <Popover.Trigger
        className="whitespace-nowrap rounded border border-outline bg-surface-elevated px-2 py-1 text-xs text-ink-muted hover:border-ink-subtle hover:text-ink"
        aria-label="Insert a value from the event"
      >
        Insert a value
      </Popover.Trigger>
      <Popover.Content align="end" aria-label="Values to insert" className="w-80 max-w-[90vw]">
        <ul className="pb-1">
          {vars.length === 0 && (
            <li className="px-3 py-2 text-[0.68rem] text-ink-subtle">
              {source === null
                ? 'This event has no registered fields yet, so there are no values to use. Register its fields on the Events screen and they show up here.'
                : `Nothing usable in ${source} yet.`}
            </li>
          )}
          {/* Provenance as SECTION HEADERS — stated once per source instead
              of suffixed onto every item. Vars arrive grouped (event first,
              then steps in document order), so headers emit on change. */}
          {vars.map((v, i) => {
            const heading = sourceHeading(v.source, source);
            const prev = i > 0 ? sourceHeading(vars[i - 1]!.source, source) : null;
            return (
              <Fragment key={v.path}>
                {heading.label !== prev?.label && (
                  <Popover.Section detail={heading.detail}>{heading.label}</Popover.Section>
                )}
                <Popover.Item hint={v.sample} onClick={() => onPick(v)}>
                  {v.label}
                </Popover.Item>
              </Fragment>
            );
          })}
        </ul>
      </Popover.Content>
    </Popover>
  );
};
